import { useContext, useState } from 'react'
import { useQueries, useQuery, UseQueryResult } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { CartContext } from '@/client/context/cart'
import { isInvalidConfigurations } from '@/client/submodules/TransparencyCalculator/helper'
import { StorageKey } from '@/client/types'
import { getTransLabelPreviews, getTransparencyBrands } from '@/infra/services'
import { getCSVInfo } from '@/infra/services/uploadFile'
import { useMutatePrices } from '../prices'

export const useGetTransparencyBrands = (customerId: number | string, locale: string = 'en') =>
  useQuery([StorageKey.TRANSPARENCY_BRANDS, locale], {
    queryFn: () => getTransparencyBrands(customerId, locale),
    enabled: customerId !== 0 && customerId !== null && customerId !== undefined && customerId !== '0',
    refetchOnWindowFocus: false
  })

//csv upload result for transparency calculator
export const useGetCSVUploadInfo = (fileName, transparencyType, cID, selection, uploadSuccess, uploadedFilesRef) => {
  const { locale, defaultLocale } = useRouter()
  const { mutate } = useMutatePrices(locale)
  const { updateSelection } = useContext(CartContext)
  // State to track the total sum of uploaded codes
  const [totalNum, setTotalNum] = useState(0)
  const { data, isLoading, isFetching, isRefetching, remove, isSuccess, status } = useQuery({
    queryKey: ['csv-result', fileName],
    queryFn: async () => {
      //Metadata for CSV processing
      const csvData = new FormData()
      csvData.append('lang', defaultLocale ? (defaultLocale === 'uk' ? 'en' : defaultLocale) : 'en')
      csvData.append('type', transparencyType)
      csvData.append('fileName', fileName)
      csvData.append('customerid', cID)
      const results = await getCSVInfo(csvData)

      // Determine new totalNum
      const newTotalNum =
        !selection.actual_quantity && !selection.upload_refresh ? results.data.num : totalNum + results.data.num

      setTotalNum(newTotalNum)

      // Ensure cart_file_item_id is an array and merge
      const updatedCartFileItemId = Array.isArray(selection.cart_file_item_id)
        ? [...selection.cart_file_item_id, results.data?.cartFileItemId]
        : [results.data?.cartFileItemId]

      const newSelection = {
        ...selection,
        upload_completed: newTotalNum === +selection.quantity ? true : false,
        upload_refresh: true,
        cart_file_item_id: updatedCartFileItemId,
        actual_quantity: selection.quantity,
        number_of_codes: results.data?.num
      }

      updateSelection(newSelection)
      mutate({ ...newSelection })
      uploadedFilesRef.current.push({
        name: fileName,
        numCodes: results.data?.num,
        price: 0,
        cartFileItem: results.data?.cartFileItemId
      })

      return results.data
    },
    enabled: uploadSuccess && !!fileName,
    keepPreviousData: false
  })
  return {
    isLoading,
    isFetching,
    isRefetching,
    remove,
    isSuccess,
    status,
    data: data,
    totalNum,
    updatedCartFileItemId: Array.isArray(selection.cart_file_item_id) ? [...selection.cart_file_item_id] : []
  }
}

type TransLabelPreview = {
  state: boolean
  img: string
  width: number
  height: number
}

export const useGetMultipleTransLabelPreviews = ({ debouncedSelection, tpyCta, tpyLayout, tpyLang, tpyPaper, cID }) => {
  const previewResults = useQueries({
    queries: Array.isArray(debouncedSelection?.sku_config)
      ? debouncedSelection.sku_config.map((skuItem) => ({
          queryKey: [
            'preview',
            skuItem.ean,
            skuItem.sku,
            skuItem.label1,
            skuItem.label2,
            skuItem.label3,
            skuItem.label4,
            skuItem.type4_bctype,
            skuItem.option,
            skuItem.brand_name,
            skuItem.lot,
            skuItem.unitCount
          ],
          queryFn: async () => {
            const formData = new FormData()
            formData.append('width', debouncedSelection.width)
            formData.append('height', debouncedSelection.height)
            formData.append('cta', tpyCta)
            formData.append('layout', tpyLayout)
            formData.append('type', debouncedSelection.transparency_type)
            formData.append('lang', tpyLang)
            formData.append('sku', skuItem.sku)
            formData.append('brandName', skuItem.brand_name)
            formData.append('ean', skuItem.ean)
            formData.append('barCodeType', skuItem.type4_bctype ?? 'code128')
            formData.append('transparent', tpyPaper ? '1' : '0')
            formData.append('label1', skuItem.label1 ?? '')
            formData.append('label2', skuItem.label2 ?? '')
            formData.append('label3', skuItem.label3 ?? '')
            formData.append('label4', skuItem.label4 ?? '')
            formData.append('option', skuItem.option ?? 'NEW')
            formData.append('logo', debouncedSelection.logoName)
            formData.append('lot', skuItem.lot ? skuItem.lot : '')
            formData.append('unitCount', skuItem.unitCount ? skuItem.unitCount : '')
            formData.append('customerId', cID ?? '')

            const response = await getTransLabelPreviews(formData)
            return response.data
          },
          enabled: !isInvalidConfigurations(debouncedSelection)
        }))
      : []
  })
  return previewResults as UseQueryResult<TransLabelPreview>[]
}
